import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { useChats } from '@communities/chats-layer/composables/useChats'
import { defineNuxtPlugin, useNuxtApp, useRouter } from 'nuxt/app'

/**
 * Reads a `conversationId` from a route query and, on desktop, opens the floating
 * chat window for it, then strips the query so it does not re-open on back/refresh.
 *
 * Mobile is a no-op here — the `conversation-deep-link` middleware never redirects
 * mobile into this query-carrying landing (it renders the conversation route), and
 * desktop-only floating windows do not exist on mobile anyway. Returns `true` when a
 * conversation was opened.
 *
 * Dependencies are injected so the helper is directly unit-testable without a Nuxt
 * runtime. The plugin below binds them to the live app.
 */
export interface ConversationDeepLinkDeps {
  isMobileOrTablet: () => boolean
  openConversationById: (conversationId: string) => Promise<void>
  /** Remove `conversationId` from the current URL (e.g. `router.replace`). */
  stripConversationQuery: () => void | Promise<void>
}

export async function openConversationFromQuery(
  query: Record<string, unknown> | undefined,
  deps: ConversationDeepLinkDeps
): Promise<boolean> {
  const raw = query?.conversationId
  const conversationId = typeof raw === 'string' ? raw.trim() : Array.isArray(raw) ? String(raw[0] ?? '').trim() : ''
  if (!conversationId) return false

  // Floating windows are desktop-only; mobile reaches the conversation route directly.
  if (deps.isMobileOrTablet()) return false

  try {
    await deps.openConversationById(conversationId)
  } catch (err) {
    console.error('[conversation-deep-link] Failed to open conversation window:', err)
  }

  await deps.stripConversationQuery()
  return true
}

export default defineNuxtPlugin(() => {
  const nuxtApp = useNuxtApp()
  const router = useRouter()

  const stripConversationQuery = async (): Promise<void> => {
    const current = router.currentRoute.value
    if (current.query?.conversationId == null) return
    const query = { ...current.query }
    delete query.conversationId
    await router.replace({ path: current.path, query, hash: current.hash })
  }

  // useChats/useGlobal are inject-based; the afterEach callback fires outside a Vue
  // effect scope, so build them lazily inside runWithContext (restores inject context).
  const deps: ConversationDeepLinkDeps = {
    isMobileOrTablet: () => Boolean(nuxtApp.runWithContext(() => useGlobal().isMobileOrTablet())),
    openConversationById: async conversationId => {
      await nuxtApp.runWithContext(() => useChats().openConversationById(conversationId))
    },
    stripConversationQuery,
  }

  // Cold start: app may have booted on the neutral landing with `?conversationId=`
  // (redirected by the conversation-deep-link middleware). Open it once ready.
  void (async () => {
    try {
      await router.isReady()
      await openConversationFromQuery(router.currentRoute.value.query, deps)
    } catch (err) {
      console.error('[conversation-deep-link] Cold-start open failed:', err)
    }
  })()

  // Warm: subsequent client navigations that land on `?conversationId=`.
  router.afterEach(to => {
    void openConversationFromQuery(to.query, deps)
  })
})
